import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, MessageSquare, Mic, Mail, DollarSign, TrendingUp, Clock, RefreshCw, Timer } from 'lucide-react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIOD_OPTIONS = [
  { label: 'Today', value: 1 },
  { label: '7 Days', value: 7 },
  { label: '30 Days', value: 30 },
  { label: '90 Days', value: 90 },
];

const EVENT_LABELS = {
  login: 'Login',
  voice_session: 'Voice Session',
  text_query: 'Text Query',
  email_send: 'Email Sent',
};

const EVENT_COLORS = {
  login: '#00e5ff',
  voice_session: '#ff2d2d',
  text_query: '#ffb74d',
  email_send: '#4caf50',
};

const PIE_COLORS = ['#00e5ff', '#ff2d2d', '#ffb74d', '#4caf50'];

function StatCard({ icon: Icon, label, value, sub, color = '#00e5ff', testId }) {
  return (
    <div className="bg-[#0d1520] border border-[#1a2a3a] rounded-lg p-4 flex items-start gap-3" data-testid={testId}>
      <div className="p-2 rounded-lg" style={{ background: `${color}15` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold font-stark tracking-wide" style={{ color }}>{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0d1520] border border-cyan-900/50 rounded-lg p-3 text-xs shadow-xl">
      <p className="text-cyan-400 font-bold mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }} className="flex justify-between gap-4">
          <span>{entry.name}:</span>
          <span className="font-bold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function UsageDashboard() {
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLog, setSelectedLog] = useState(null);
  const [conversationData, setConversationData] = useState(null);
  const [loadingConversation, setLoadingConversation] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, dailyRes, usersRes, activityRes] = await Promise.all([
        axios.get(`${API}/admin/usage/summary?days=${days}`, { headers }),
        axios.get(`${API}/admin/usage/daily?days=${days}`, { headers }),
        axios.get(`${API}/admin/usage/users?days=${days}`, { headers }),
        axios.get(`${API}/admin/usage/activity?limit=100`, { headers }),
      ]);
      setSummary(summaryRes.data);
      setDaily(dailyRes.data);
      setUsers(usersRes.data);
      setActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openConversation = async (evt) => {
    if (!evt.has_conversation) return;
    setSelectedLog(evt);
    setLoadingConversation(true);
    try {
      const res = await axios.get(`${API}/admin/usage/conversation/${evt.log_id}`, { headers });
      setConversationData(res.data);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      setConversationData(null);
    } finally {
      setLoadingConversation(false);
    }
  };

  const pieData = summary ? [
    { name: 'Voice', value: summary.voice_sessions },
    { name: 'Text', value: summary.text_queries },
    { name: 'Logins', value: summary.logins },
    { name: 'Emails', value: summary.emails_sent },
  ].filter(d => d.value > 0) : [];

  const totalInteractions = summary ? summary.voice_sessions + summary.text_queries : 0;

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-200" data-testid="usage-dashboard">
      {/* Header */}
      <div className="border-b border-[#1a2a3a] bg-[#0d1520]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-cyan-400/70 hover:text-cyan-300 transition-colors" data-testid="back-to-voice">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h1 className="text-lg font-stark tracking-wider text-cyan-400">USAGE MONITOR</h1>
          </div>
          <div className="flex items-center gap-2">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={`px-3 py-1 text-xs rounded-full font-medium transition-all ${
                  days === opt.value
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50'
                    : 'text-gray-500 hover:text-gray-300 border border-transparent'
                }`}
                data-testid={`period-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
            <button onClick={fetchData} className="ml-2 text-cyan-400/50 hover:text-cyan-300 transition-colors p-1" data-testid="refresh-usage">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {loading && !summary ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatCard icon={Users} label="Active Users" value={summary?.active_users || 0} testId="stat-active-users" />
              <StatCard icon={Mic} label="Voice Sessions" value={summary?.voice_sessions || 0} color="#ff2d2d" testId="stat-voice-sessions" />
              <StatCard icon={Timer} label="Voice Minutes" value={summary?.voice_duration?.total_minutes || 0} sub={summary?.voice_duration?.completed_sessions ? `avg ${summary.voice_duration.avg_seconds}s` : ''} color="#ff6b6b" testId="stat-voice-minutes" />
              <StatCard icon={MessageSquare} label="Text Queries" value={summary?.text_queries || 0} color="#ffb74d" testId="stat-text-queries" />
              <StatCard icon={Mail} label="Emails Sent" value={summary?.emails_sent || 0} color="#4caf50" testId="stat-emails-sent" />
              <StatCard icon={TrendingUp} label="Total Tokens" value={(summary?.tokens?.total_tokens || 0).toLocaleString()} testId="stat-total-tokens" />
              <StatCard icon={DollarSign} label="Est. Cost" value={`$${summary?.estimated_cost_usd || '0.00'}`} color="#e040fb" testId="stat-estimated-cost" />
            </div>

            {/* Tab Nav */}
            <div className="flex gap-1 border-b border-[#1a2a3a]">
              {['overview', 'users', 'activity'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium transition-all border-b-2 ${
                    activeTab === tab
                      ? 'text-cyan-400 border-cyan-400'
                      : 'text-gray-500 border-transparent hover:text-gray-300'
                  }`}
                  data-testid={`tab-${tab}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Daily Activity Chart */}
                <div className="lg:col-span-2 bg-[#0d1520] border border-[#1a2a3a] rounded-lg p-4" data-testid="daily-chart">
                  <h3 className="text-sm font-stark text-cyan-400/80 tracking-wider mb-4">DAILY ACTIVITY</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={daily} barGap={2}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
                      <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill: '#555', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="voice_sessions" name="Voice" fill="#ff2d2d" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="text_queries" name="Text" fill="#ffb74d" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="logins" name="Logins" fill="#00e5ff" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="emails_sent" name="Emails" fill="#4caf50" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Interaction Breakdown Pie */}
                <div className="bg-[#0d1520] border border-[#1a2a3a] rounded-lg p-4" data-testid="breakdown-chart">
                  <h3 className="text-sm font-stark text-cyan-400/80 tracking-wider mb-4">INTERACTION MIX</h3>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                          {pieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-gray-600 text-sm">No data yet</div>
                  )}
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {pieData.map((d, i) => (
                      <span key={d.name} className="flex items-center gap-1.5 text-xs text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} />
                        {d.name}: {d.value}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Token Usage Trend */}
                <div className="lg:col-span-3 bg-[#0d1520] border border-[#1a2a3a] rounded-lg p-4" data-testid="token-info">
                  <h3 className="text-sm font-stark text-cyan-400/80 tracking-wider mb-3">TOKEN USAGE</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Prompt Tokens</p>
                      <p className="text-lg font-bold text-cyan-400">{(summary?.tokens?.total_prompt_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Completion Tokens</p>
                      <p className="text-lg font-bold text-amber-400">{(summary?.tokens?.total_completion_tokens || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Estimated Cost</p>
                      <p className="text-lg font-bold text-purple-400">${summary?.estimated_cost_usd || '0.00'}</p>
                      <p className="text-[10px] text-gray-600">GPT-4o: $2.50/1M in, $10/1M out</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-[#0d1520] border border-[#1a2a3a] rounded-lg overflow-hidden" data-testid="users-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#1a2a3a] text-left">
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium">User</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-center">Logins</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-center">Voice</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-center">Voice Min</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-center">Text</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-center">Emails</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-right">Tokens</th>
                        <th className="px-4 py-3 text-xs text-cyan-400/70 uppercase tracking-wider font-medium text-right">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-600">No user activity in this period</td></tr>
                      ) : users.map((u, i) => (
                        <tr key={u.username} className="border-b border-[#1a2a3a]/50 hover:bg-cyan-500/5 transition-colors" data-testid={`user-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-gray-200">{u.username}</td>
                          <td className="px-4 py-3 text-center text-cyan-400">{u.logins}</td>
                          <td className="px-4 py-3 text-center text-red-400">{u.voice_sessions}</td>
                          <td className="px-4 py-3 text-center text-red-300">{u.voice_minutes || 0}</td>
                          <td className="px-4 py-3 text-center text-amber-400">{u.text_queries}</td>
                          <td className="px-4 py-3 text-center text-green-400">{u.emails_sent}</td>
                          <td className="px-4 py-3 text-right text-gray-400">{(u.total_tokens || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-gray-500 text-xs">
                            {u.last_active ? new Date(u.last_active).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === 'activity' && (
              <div className="bg-[#0d1520] border border-[#1a2a3a] rounded-lg overflow-hidden" data-testid="activity-feed">
                <div className="max-h-[600px] overflow-y-auto">
                  {activity.length === 0 ? (
                    <div className="px-4 py-12 text-center text-gray-600">No activity recorded yet</div>
                  ) : activity.map((evt, i) => (
                    <div
                      key={i}
                      onClick={() => evt.has_conversation && openConversation(evt)}
                      className={`flex items-center gap-3 px-4 py-2.5 border-b border-[#1a2a3a]/30 transition-colors ${
                        evt.has_conversation ? 'cursor-pointer hover:bg-cyan-500/10' : 'hover:bg-cyan-500/5'
                      }`}
                      data-testid={`activity-row-${i}`}
                    >
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: EVENT_COLORS[evt.type] || '#555' }} />
                      <span className="text-xs font-medium min-w-[80px]" style={{ color: EVENT_COLORS[evt.type] || '#888' }}>
                        {EVENT_LABELS[evt.type] || evt.type}
                      </span>
                      <span className="text-sm text-gray-300 flex-1">{evt.username}</span>
                      {evt.has_conversation && (
                        <span className="text-[10px] text-cyan-500/60 border border-cyan-500/30 rounded px-1.5 py-0.5">VIEW</span>
                      )}
                      {evt.metadata?.total_tokens > 0 && (
                        <span className="text-xs text-gray-500">{evt.metadata.total_tokens} tokens</span>
                      )}
                      {evt.metadata?.duration_seconds != null && evt.metadata.duration_seconds > 0 && (
                        <span className="text-xs text-gray-500">{Math.round(evt.metadata.duration_seconds)}s</span>
                      )}
                      {evt.metadata?.recipient && (
                        <span className="text-xs text-gray-500">to {evt.metadata.recipient}</span>
                      )}
                      <span className="text-xs text-gray-600 flex-shrink-0 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(evt.timestamp).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conversation Modal */}
            {selectedLog && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setSelectedLog(null); setConversationData(null); }}>
                <div className="bg-[#0d1520] border border-cyan-900/50 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="conversation-modal">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-5 py-3 border-b border-[#1a2a3a]">
                    <div>
                      <h3 className="text-sm font-stark text-cyan-400 tracking-wider">SESSION TRANSCRIPT</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {selectedLog.username} &middot; {EVENT_LABELS[selectedLog.type] || selectedLog.type} &middot; {new Date(selectedLog.timestamp).toLocaleString()}
                        {conversationData?.duration_seconds ? ` · ${Math.round(conversationData.duration_seconds)}s` : ''}
                      </p>
                    </div>
                    <button onClick={() => { setSelectedLog(null); setConversationData(null); }} className="text-gray-500 hover:text-gray-300 text-lg px-2" data-testid="close-conversation-modal">&times;</button>
                  </div>
                  {/* Modal Body */}
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {loadingConversation ? (
                      <div className="flex items-center justify-center py-10">
                        <RefreshCw className="h-5 w-5 text-cyan-400 animate-spin" />
                      </div>
                    ) : conversationData?.conversation?.length > 0 ? (
                      conversationData.conversation.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm ${
                            msg.role === 'user'
                              ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-500/20'
                              : 'bg-[#1a2a3a] text-gray-300 border border-[#2a3a4a]'
                          }`}>
                            <p className="text-[10px] font-medium mb-1" style={{ color: msg.role === 'user' ? '#00e5ff' : '#ffb74d' }}>
                              {msg.role === 'user' ? 'USER' : 'AEDA'}
                            </p>
                            <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-600 py-10 text-sm">No conversation data available for this session</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
